import React, { useEffect, useState, useCallback } from 'react';
import { 
  Mail, Settings, Play, CheckCircle, AlertCircle, 
  Eye, Edit2, RotateCcw, Send, Terminal, Code2, 
  ChevronRight, Layout, Cpu, Globe, Zap, FileText,
  UserPlus, Loader, ShieldCheck, RefreshCw
} from 'lucide-react';
import api from '../../services/api';
import PageHeader from '../../components/PageHeader';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';

const TEMPLATE_TABS = [
    { key: 'welcome', label: 'Onboarding Sequence', icon: UserPlus, desc: 'Triggered upon initial entity registration' },
    { key: 'subscriptionActivated', label: 'Billing Confirmation', icon: Zap, desc: 'Triggered after fiscal credit settlement' },
    { key: 'subscriptionExpiring', label: 'Service Termination Alert', icon: AlertCircle, desc: 'Triggered prior to protocol expiration' },
];

const PLACEHOLDERS = {
    welcome: ['companyName', 'email', 'planName', 'endDate', 'loginUrl', 'platformName', 'year'],
    subscriptionActivated: ['companyName', 'planName', 'amount', 'endDate', 'maxDevices', 'messageLimit', 'dashboardUrl', 'platformName', 'year'],
    subscriptionExpiring: ['companyName', 'planName', 'daysLeft', 'endDate', 'renewUrl', 'platformName', 'year'],
};

export default function SuperAdminEmail() {
    const [activeTab, setActiveTab] = useState('welcome');
    const [templates, setTemplates] = useState({});
    const [editSubject, setEditSubject] = useState('');
    const [editHtml, setEditHtml] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testEmail, setTestEmail] = useState('');
    const [showPreview, setShowPreview] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get('/admin/email-templates');
            setTemplates(res.data.templates || {});
        } catch (e) {
            alertService.error('Network Failure', e.response?.data?.error || 'Failed to sync bridge templates.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const t = templates[activeTab];
        if (t) {
            setEditSubject(t.subject || '');
            setEditHtml(t.html || '');
        }
    }, [activeTab, templates]);

    const handleSave = async () => {
        try {
            setSaving(true);
            await api.put('/admin/email-templates', {
                templateKey: activeTab,
                subject: editSubject,
                html: editHtml,
            });
            alertService.toast('success', 'Email parameters updated successfully');
            load();
        } catch (e) {
            alertService.error('Update Failed', e.response?.data?.error || 'Failed to preserve broadcast protocol.');
        } finally {
            setSaving(false);
        }
    };

    const handleReset = async () => {
        const confirmed = await alertService.confirm(
            'Restore the default template?',
            'This will purge all custom markup and revert to the system master default.',
            'danger',
            'Revert to Default'
        );

        if (!confirmed) return;

        try {
            setSaving(true);
            await api.delete(`/admin/email-templates/${activeTab}`);
            alertService.toast('success', 'Default template restored');
            load();
        } catch (e) {
            alertService.error('Reset Failure', e.response?.data?.error || 'Failed to revert template logic.');
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async () => {
        if (!testEmail) return alertService.error('Address Required', 'Specify target coordinates for verification.');
        try {
            setTesting(true);
            const res = await api.post('/admin/email-templates/test', {
                to: testEmail,
                templateKey: activeTab,
            });
            alertService.toast('success', 'Verification signal dispatched successfully');
        } catch (e) {
            alertService.error('Test email failed', e.response?.data?.error || 'Verification failure. Check SMTP gateway config.');
        } finally {
            setTesting(false);
        }
    };

    const currentTabInfo = TEMPLATE_TABS.find(t => t.key === activeTab);
    const isCustomized = templates[activeTab]?.isCustomized;

    return (
    <div className="w-full pb-10 animate-in fade-in slide-in-from-bottom-2 duration-500">
            <PageHeader title="Email Templates" icon={Mail} subtitle="Configure automated communication templates for platform emails." />

            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div className="flex items-center gap-3">
                   <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#E0F2FE] border border-[#BAE6FD] text-[12px] font-bold text-[#0176D3]">
                      <ShieldCheck size={14} /> Secure Access Mode
                   </div>
                   {isCustomized && (
                      <Badge variant="blue" className="bg-[#0176D3] text-white text-[11px] font-semibold">Custom Override</Badge>
                   )}
                </div>
                <div className="flex gap-2">
                    {isCustomized && (
                        <button 
                            className="h-9 px-4 bg-white border border-[#C9C9C9] text-[#080707] text-[13px] font-semibold rounded whitespace-nowrap hover:bg-[#F3F2F2] transition-colors flex items-center justify-center disabled:opacity-50"
                            onClick={handleReset} 
                            disabled={saving}
                        >
                            Revert to Default
                        </button>
                    )}
                    <button 
                        className="h-9 px-4 bg-[#0176D3] hover:bg-[#014486] text-white text-[13px] font-semibold rounded whitespace-nowrap flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                        onClick={handleSave} 
                        disabled={saving}
                    >
                        {saving ? <Loader size={16} className="animate-spin" /> : null}
                        Save Template
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="w-full py-40 flex flex-col items-center justify-center gap-6 opacity-50">
                   <Loader size={40} className="animate-spin text-[#0176D3]" />
                   <p className="text-[13px] font-semibold text-[#706E6B] animate-pulse">Syncing Communication Nodes…</p>
                </div>
            ) : (
                <div className="flex flex-col lg:flex-row gap-8">
                    
                    {/* Logic Matrix Sidebar */}
                    <div className="w-full lg:w-80 flex flex-col gap-6">
                        <Card className="p-2 border-[#E5E5E5] shadow-[0_1px_3px_rgba(0,0,0,0.08)] bg-white rounded-lg">
                            <div className="px-4 py-3 text-[12px] font-bold text-[#444444] uppercase border-b border-[#D8DDE6] mb-2 flex items-center gap-2">
                               <div className="w-2 h-2 rounded-full bg-[#0176D3]" /> Dispatch Registry
                            </div>
                            <div className="flex lg:flex-col gap-1 overflow-x-auto">
                                {TEMPLATE_TABS.map(t => (
                                    <button
                                        key={t.key}
                                        className={cn(
 "shrink-0 whitespace-nowrap lg:w-full flex items-center gap-3 px-4 h-9 rounded transition-colors relative text-left",
 activeTab === t.key ? 'bg-[#F4F6F9] text-[#0176D3] font-semibold' : 'text-[#080707] font-semibold hover:bg-[#F3F2F2]'
 )}
                                        onClick={() => { setActiveTab(t.key); setShowPreview(false); }}
                                    >
                                        <t.icon size={18} className={activeTab === t.key ? 'text-[#0176D3]' : 'text-[#706E6B]'} />
                                        <div className="flex flex-col items-start">
                                           <span className="text-[13px]">{t.label}</span>
                                        </div>
                                        {activeTab === t.key && <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#0176D3] rounded-r-md" />}
                                    </button>
                                ))}
                            </div>
                        </Card>

                        {/* Verification Block */}
                        <Card className="p-6 border-[#E5E5E5] shadow-[0_1px_3px_rgba(0,0,0,0.08)] bg-[#F4F6F9] rounded-lg relative overflow-hidden group">
                            <div className="absolute top-0 right-0 p-4 opacity-5 text-[#0176D3] pointer-events-none -mr-4 -mt-4">
                               <Send size={80} />
                            </div>
                            <div className="space-y-2 relative z-10">
                                <div className="text-[14px] font-bold text-[#0176D3] flex items-center gap-2">
                                   <Terminal size={16} /> Send Test Email
                                </div>
                                <p className="text-[12px] text-[#706E6B] leading-relaxed">Test dispatch delivery with dynamic parameter injection.</p>
                            </div>
                            <div className="space-y-4 mt-6 relative z-10">
                                <Input
                                    icon={Mail}
                                    type="email"
                                    className="text-[13px]"
                                    value={testEmail}
                                    onChange={e => setTestEmail(e.target.value)}
                                    placeholder="Target address identity…"
                                />
                                <button
                                    className="w-full h-9 px-4 bg-[#0176D3] hover:bg-[#014486] text-white text-[13px] font-semibold rounded whitespace-nowrap flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                                    onClick={handleTest}
                                    disabled={!testEmail || testing}
                                >
                                    {testing ? <Loader size={16} className="animate-spin" /> : <Send size={16} />}
                                    Verify Delivery
                                </button>
                            </div>
                        </Card>
                    </div>

                    {/* Protocol Editor Nexus */}
                    <div className="flex-1 min-w-0">
                        <Card className="p-0 overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.08)] bg-white border-[#E5E5E5] rounded-lg">
                            <div className="p-6 border-b border-[#D8DDE6] flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 bg-[#F4F6F9]">
                                <div className="flex items-center gap-4">
                                   <div className="w-12 h-12 rounded-md bg-[#E0F2FE] border border-[#BAE6FD] flex items-center justify-center text-[#0176D3]">
                                      <currentTabInfo.icon size={24} />
                                   </div>
                                   <div>
                                       <h3 className="text-xl font-bold text-[#080707] tracking-tight">{currentTabInfo?.label}</h3>
                                       <div className="flex items-center gap-2 mt-1">
                                          <div className="w-2 h-2 rounded-full bg-[#0176D3]" />
                                          <span className="text-[12px] text-[#706E6B]">{currentTabInfo?.desc}</span>
                                       </div>
                                   </div>
                                </div>
                                <div className="flex bg-[#F3F2F2] p-1 rounded-md border border-[#D8DDE6]">
                                   <button
                                       className={cn(
 "flex items-center gap-2 px-4 py-2 rounded whitespace-nowrap text-[13px] font-semibold transition-colors",
 !showPreview ? 'bg-white text-[#080707] shadow-sm' : 'text-[#444444] hover:text-[#080707]'
 )}
                                       onClick={() => setShowPreview(false)}
                                   >
                                       <Edit2 size={14} /> Editor
                                   </button>
                                   <button
                                       className={cn(
 "flex items-center gap-2 px-4 py-2 rounded whitespace-nowrap text-[13px] font-semibold transition-colors",
 showPreview ? 'bg-white text-[#080707] shadow-sm' : 'text-[#444444] hover:text-[#080707]'
 )}
                                       onClick={() => setShowPreview(true)}
                                   >
                                       <Eye size={14} /> Preview
                                   </button>
                                </div>
                            </div>

                            <div className="p-6">
                                {!showPreview ? (
                                    <div className="space-y-6">
                                        <div className="space-y-2">
                                            <label className="text-[12px] font-semibold text-[#444444] flex items-center gap-2">
                                               <Terminal size={14} className="text-[#0176D3]" /> Outbound Subject Line
                                            </label>
                                            <Input
                                                className="text-[13px]"
                                                value={editSubject}
                                                onChange={e => setEditSubject(e.target.value)}
                                                placeholder="Enter broadcast subject…"
                                            />
                                        </div>

                                        <div className="p-4 rounded-md bg-[#F4F6F9] border border-[#D8DDE6] relative overflow-hidden group">
                                            <div className="absolute top-0 right-0 p-4 opacity-5 text-[#0176D3] pointer-events-none -mr-4 -mt-4"><Code2 size={60} /></div>
                                            <div className="text-[12px] font-bold text-[#0176D3] flex items-center gap-2 relative z-10">
                                               <Cpu size={14} /> Dynamic Variables
                                            </div>
                                            <div className="flex flex-wrap gap-2 mt-4 relative z-10">
                                                {(PLACEHOLDERS[activeTab] || []).map(p => (
                                                    <code key={p} className="px-2.5 py-1 rounded-lg bg-white border border-[#E5E5E5] text-[#0176D3] font-mono text-[12px]">
                                                        {`{{${p}}}`}
                                                    </code>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-[12px] font-semibold text-[#444444] flex items-center gap-2">
                                               <Layout size={14} className="text-[#0176D3]" /> Dispatch Body Markup (HTML)
                                            </label>
                                            <div className="relative rounded-lg overflow-hidden border border-[#E5E5E5] bg-white focus-within:border-[#0176D3] transition-colors shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
                                               <textarea
                                                   className="w-full p-4 min-h-[400px] bg-transparent text-[#080707] font-mono text-[13px] resize-none focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                                                   value={editHtml}
                                                   onChange={e => setEditHtml(e.target.value)}
                                                   spellCheck="false"
                                               />
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-6 animate-in fade-in duration-300">
                                       <div className="p-4 rounded-md bg-[#F4F6F9] border border-[#D8DDE6]">
                                          <div className="text-[12px] text-[#444444] flex items-center gap-2">
                                             <div className="font-semibold flex items-center gap-1.5"><RefreshCw size={14} className="text-[#0176D3]" /> Subject:</div>
                                             <span className="text-[#080707]">{editSubject.replace(/\{\{(\w+)\}\}/g, (_, k) => `[${k}]`)}</span>
                                          </div>
                                       </div>
                                        
                                        <div className="rounded-lg overflow-hidden border border-[#E5E5E5] bg-white min-h-[500px] relative shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
                                           <div className="h-10 bg-[#F3F2F2] flex items-center justify-between px-4 border-b border-[#D8DDE6]">
                                              <div className="flex gap-2">
                                                 <div className="w-3 h-3 rounded-full bg-[#E53E3E]" />
                                                 <div className="w-3 h-3 rounded-full bg-[#D69E2E]" />
                                                 <div className="w-3 h-3 rounded-full bg-[#38A169]" />
                                              </div>
                                              <div className="text-[11px] font-mono text-[#706E6B]">PREVIEW</div>
                                           </div>
                                           <div 
                                              className="w-full h-full overflow-auto bg-white p-6"
                                              dangerouslySetInnerHTML={{
                                                  __html: editHtml
                                                      .replace(/\{\{companyName\}\}/g, 'Titanium Enterprise')
                                                      .replace(/\{\{email\}\}/g, 'admin@titanium.com')
                                                      .replace(/\{\{planName\}\}/g, 'PLATINUM TIER')
                                                      .replace(/\{\{endDate\}\}/g, '2026-12-31')
                                                      .replace(/\{\{amount\}\}/g, '4,999.00')
                                                      .replace(/\{\{maxDevices\}\}/g, '50')
                                                      .replace(/\{\{messageLimit\}\}/g, 'UNLIMITED')
                                                      .replace(/\{\{daysLeft\}\}/g, '7')
                                                      .replace(/\{\{loginUrl\}\}/g, '#')
                                                      .replace(/\{\{dashboardUrl\}\}/g, '#')
                                                      .replace(/\{\{renewUrl\}\}/g, '#')
                                                      .replace(/\{\{platformName\}\}/g, 'WaapLeads')
                                                      .replace(/\{\{year\}\}/g, new Date().getFullYear().toString())
                                              }}
                                           />
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="p-4 bg-[#F4F6F9] border-t border-[#D8DDE6] flex items-center justify-between text-[11px] font-semibold text-[#706E6B]">
                               <div className="flex items-center gap-2">
                                  <Globe size={14} className="text-[#0176D3]" /> <span>Mail relay: online</span>
                                </div>
                               <div className="font-mono">Ref: SYSTEM_BROADCAST_NODE_{activeTab.toUpperCase()}</div>
                            </div>
                        </Card>
                    </div>
                </div>
            )}
        </div>
    );
}
